/**
 * Super-admin support chat console.
 *
 * Two tabs:
 *  - Live sessions: lists all sessions, click to open the conversation,
 *    Take over / Release controls, real-time via WebSocket on the same
 *    /api/chatbot/ws/{session_id} endpoint as the tenant widget.
 *  - Config: model dropdown, reply delay, prompt extras, fallback message,
 *    greeting, multilingual toggle, enable/disable.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Headset, RefreshCw, Settings as Cog, Send, MessageCircle, Power, Save } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { PageHeader } from "@/components/Ui";

export default function AdminChatbot() {
    const [tab, setTab] = useState("sessions");
    return (
        <>
            <PageHeader title="Support chats" subtitle="Live in-app chats from tenants. Configure the AI assistant or take over any conversation in real time."/>
            <div className="flex gap-2 border-b border-border mb-6">
                {[
                    { id: "sessions", label: "Live sessions", Icon: MessageCircle },
                    { id: "config",   label: "Config",        Icon: Cog },
                ].map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`chatbot-tab-${t.id}`}
                        className={`px-4 py-2.5 text-xs uppercase tracking-[0.16em] border-b-2 -mb-px inline-flex items-center gap-2 ${
                            tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}>
                        <t.Icon className="w-3.5 h-3.5" strokeWidth={1.5}/>{t.label}
                    </button>
                ))}
            </div>
            {tab === "sessions" ? <SessionsPanel/> : <ConfigPanel/>}
        </>
    );
}

function SessionsPanel() {
    const [sessions, setSessions] = useState([]);
    const [active, setActive] = useState(null);  // {id, ...}
    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/chatbot/sessions?limit=50"); setSessions(data); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);  // eslint-disable-line
    return (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
            <div className="border border-border overflow-y-auto max-h-[70vh]">
                <header className="px-3 py-2 bg-muted flex items-center justify-between">
                    <div className="overline">{sessions.length} sessions</div>
                    <button onClick={load} className="text-muted-foreground hover:text-primary" data-testid="chatbot-refresh">
                        <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5}/>
                    </button>
                </header>
                {sessions.length === 0 && <div className="px-3 py-6 text-center text-muted-foreground text-xs">No active chats yet.</div>}
                {sessions.map(s => (
                    <button key={s.id} onClick={() => setActive(s)} data-testid={`chatbot-session-${s.id}`}
                        className={`block w-full text-left px-3 py-3 border-t border-border hover:bg-muted/80 ${active?.id === s.id ? "bg-muted" : ""}`}>
                        <div className="flex items-center justify-between mb-1">
                            <div className="text-sm text-foreground truncate">{s.user_name || s.user_email}</div>
                            {s.unread_for_admin && <span className="text-[9px] bg-primary text-primary-foreground px-1.5 py-0.5 uppercase">new</span>}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">{s.user_email} · {s.user_role}</div>
                        <div className="flex items-center justify-between mt-1">
                            <span className={`text-[10px] uppercase tracking-[0.16em] ${s.status === "admin_takeover" ? "text-primary" : "text-muted-foreground"}`}>{s.status === "admin_takeover" ? "live · agent" : "bot"}</span>
                            <span className="text-[10px] text-muted-foreground">{s.message_count} msgs</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground">{fmtDateTime(s.last_activity_at)}</div>
                    </button>
                ))}
            </div>
            {active ? <SessionConversation key={active.id} session={active} onChange={load}/> : (
                <div className="border border-border flex items-center justify-center text-muted-foreground text-sm py-20">
                    Pick a session on the left to read along or take over.
                </div>
            )}
        </div>
    );
}

function SessionConversation({ session, onChange }) {
    const [msgs, setMsgs] = useState([]);
    const [draft, setDraft] = useState("");
    const [status, setStatus] = useState(session.status);
    const [typing, setTyping] = useState(null);
    const wsRef = useRef(null);
    const scrollRef = useRef(null);

    useEffect(() => {  // eslint-disable-line
        let active = true;
        (async () => {
            try {
                const { data } = await api.get(`/chatbot/sessions/${session.id}/messages`);
                if (active) { setMsgs(data.messages || []); setStatus(data.status); }
            } catch (e) { toast.error(apiError(e)); }
            const { data: t } = await api.post("/auth/ws-token");
            if (!active) return;
            const wsBase = API.replace(/^http/, "ws").replace(/\/api$/, "");
            const ws = new WebSocket(`${wsBase}/api/chatbot/ws/${session.id}?token=${encodeURIComponent(t.token)}`);
            wsRef.current = ws;
            ws.onmessage = (e) => {
                try {
                    const d = JSON.parse(e.data);
                    if (d.type === "message") { setMsgs(p => [...p, d.message]); setTyping(null); }
                    else if (d.type === "typing" && d.from === "user") {
                        setTyping("user");
                        setTimeout(() => setTyping(null), 3000);
                    }
                } catch (e) { console.warn("Chat WS frame parse failed:", e); }
            };
        })();
        return () => { active = false; if (wsRef.current) wsRef.current.close(); };
    }, [session.id]);

    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, typing]);

    const send = (e) => {
        e.preventDefault();
        const text = draft.trim();
        if (!text || !wsRef.current || wsRef.current.readyState !== 1) return;
        wsRef.current.send(JSON.stringify({ type: "message", content: text }));
        setDraft("");
    };

    const takeover = async () => {
        try { await api.post(`/admin/chatbot/sessions/${session.id}/takeover`); setStatus("admin_takeover"); onChange?.(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const release = async () => {
        try { await api.post(`/admin/chatbot/sessions/${session.id}/release`); setStatus("bot"); onChange?.(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="border border-border flex flex-col max-h-[70vh]" data-testid="chatbot-conversation">
            <header className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted">
                <div>
                    <div className="text-sm text-foreground">{session.user_name || session.user_email}</div>
                    <div className="text-[10px] text-muted-foreground">{session.user_role} · {status === "admin_takeover" ? "you are on this chat" : "bot is replying"}</div>
                </div>
                {status === "admin_takeover" ? (
                    <button onClick={release} data-testid="chatbot-release"
                        className="text-xs uppercase tracking-[0.16em] text-muted-foreground hover:text-rose-400 px-3 py-1.5 border border-border inline-flex items-center gap-1">
                        <Power className="w-3 h-3" strokeWidth={1.5}/>Release to bot
                    </button>
                ) : (
                    <button onClick={takeover} data-testid="chatbot-takeover"
                        className="text-xs uppercase tracking-[0.16em] text-primary hover:text-primary px-3 py-1.5 border border-primary inline-flex items-center gap-1">
                        <Headset className="w-3 h-3" strokeWidth={1.5}/>Take over
                    </button>
                )}
            </header>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 text-xs">
                {msgs.map(m => (
                    <div key={m.id} className={`px-3 py-2 max-w-[80%] ${
                        m.role === "user" ? "bg-muted border border-border ml-auto"
                        : m.role === "system" ? "bg-primary/10 border border-primary/30 text-center text-[10px] uppercase tracking-[0.16em] text-primary"
                        : m.role === "admin" ? "bg-primary/10 border border-primary/30"
                        : "bg-background border border-border"
                    } whitespace-pre-wrap`}>
                        {m.role !== "system" && <div className="text-[9px] text-muted-foreground mb-0.5 uppercase tracking-[0.16em]">{m.role} · {m.author_name || ""}</div>}
                        {m.content}
                    </div>
                ))}
                {typing && <div className="text-[10px] text-muted-foreground italic">User is typing…</div>}
            </div>
            {status === "admin_takeover" && (
                <form onSubmit={send} className="border-t border-border p-2 flex gap-2">
                    <input value={draft} onChange={e => setDraft(e.target.value)} placeholder="Reply as a human teammate…"
                        data-testid="chatbot-admin-input"
                        className="flex-1 bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
                    <button type="submit" disabled={!draft.trim()} data-testid="chatbot-admin-send"
                        className="bg-primary text-primary-foreground px-3 disabled:opacity-30"><Send className="w-3.5 h-3.5"/></button>
                </form>
            )}
        </div>
    );
}

function ConfigPanel() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);     // assembled prompt preview
    const [previewRole, setPreviewRole] = useState("junior_advocate");
    const [previewOpen, setPreviewOpen] = useState(false);
    const [learning, setLearning] = useState(false);
    const [analytics, setAnalytics] = useState(null);
    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/chatbot/config"); setCfg(data); }
        catch (e) { toast.error(apiError(e)); }
        try { const { data } = await api.get("/admin/chatbot/analytics?days=7"); setAnalytics(data); }
        catch (e) { console.warn("Chatbot analytics fetch failed (optional):", e); }
    }, []);
    useEffect(() => { load(); }, [load]);  // eslint-disable-line
    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/admin/chatbot/config", {
                enabled: cfg.enabled, model: cfg.model, reply_delay_ms: cfg.reply_delay_ms,
                max_tokens: cfg.max_tokens,
                system_prompt_base: cfg.system_prompt_base || "",
                system_prompt_extra: cfg.system_prompt_extra,
                fallback_message: cfg.fallback_message, greeting: cfg.greeting,
                multilingual: cfg.multilingual, typo_correction: cfg.typo_correction,
                learn_after_release: !!cfg.learn_after_release,
                learned_notes: cfg.learned_notes || "",
            });
            toast.success("Chatbot config saved");
            await load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };
    const loadPreview = async (role = previewRole) => {
        try {
            const { data } = await api.get(`/admin/chatbot/system-prompt-preview?role=${role}`);
            setPreview(data); setPreviewOpen(true);
        } catch (e) { toast.error(apiError(e)); }
    };
    const runLearn = async () => {
        setLearning(true);
        try {
            const { data } = await api.post("/admin/chatbot/learn-from-transcripts?days=30");
            toast.success(`Learned from ${data.samples} replies`);
            await load();
        } catch (e) { toast.error(apiError(e)); } finally { setLearning(false); }
    };
    const clearLearned = async () => {
        if (!window.confirm("Clear all learned tone notes? The bot reverts to its default tone.")) return;
        try {
            await api.delete("/admin/chatbot/learned-notes");
            toast.success("Learned notes cleared");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };
    if (!cfg) return <div className="text-sm text-muted-foreground py-8">Loading…</div>;
    return (
        <div className="space-y-4 max-w-3xl">
            {analytics && (
                <div className="border border-border bg-card p-4" data-testid="chatbot-analytics">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-xs text-foreground">Last 7 days · health</div>
                            <div className="text-[10px] text-muted-foreground">Use this to see if the self-learning loop is improving answer quality. Metadata only — no PII.</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
                        <Stat label="Sessions" value={analytics.sessions_total} testId="chatbot-stat-sessions"/>
                        <Stat label="Deflection" value={analytics.deflection_rate == null ? "—" : `${analytics.deflection_rate}%`} testId="chatbot-stat-deflection" hint="% sessions resolved by bot without human takeover"/>
                        <Stat label="Bot replies" value={analytics.bot_replies} testId="chatbot-stat-replies"/>
                        <Stat label="Avg tokens" value={analytics.avg_tokens_per_reply} testId="chatbot-stat-tokens" hint="Lower = more token-conscious"/>
                        <Stat label="User msgs" value={analytics.user_messages} testId="chatbot-stat-usermsgs"/>
                    </div>
                    {analytics.top_questions?.length > 0 && (
                        <div className="mt-3 border-t border-border pt-3">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Top question starts</div>
                            <div className="flex flex-wrap gap-1.5">
                                {analytics.top_questions.map((q, idx) => (
                                    <span key={idx} className="text-[10px] bg-muted border border-border px-2 py-1" data-testid={`chatbot-top-q-${idx}`}>
                                        “{q.head}…” <span className="text-primary">×{q.count}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="bg-muted border border-border p-4 flex items-center justify-between">
                <div>
                    <div className="text-sm text-foreground">Chatbot is {cfg.enabled ? "ON" : "OFF"} for all tenants</div>
                    <div className="text-[10px] text-muted-foreground mt-1">When off, the widget hides for everyone. The Live sessions tab still works for historical chats.</div>
                </div>
                <button onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })} data-testid="chatbot-toggle-enabled"
                    className={`w-12 h-6 ${cfg.enabled ? "bg-primary" : "bg-border"} relative transition`}>
                    <span className={`block w-5 h-5 bg-white absolute top-0.5 transition ${cfg.enabled ? "left-6" : "left-0.5"}`}/>
                </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <Field label="OpenAI model">
                    <select value={cfg.model} onChange={e => setCfg({ ...cfg, model: e.target.value })} data-testid="chatbot-model"
                        className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary">
                        {(cfg.available_models || []).map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                </Field>
                <Field label="Reply delay (ms)" hint="Bot 'thinking' time before reply starts. 0 = instant.">
                    <input type="number" min="0" max="10000" value={cfg.reply_delay_ms || 0}
                        onChange={e => setCfg({ ...cfg, reply_delay_ms: parseInt(e.target.value) || 0 })}
                        data-testid="chatbot-delay"
                        className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary"/>
                </Field>
                <Field label="Max tokens / reply" hint="50–4096. Higher = longer answers, more cost.">
                    <input type="number" min="50" max="4096" value={cfg.max_tokens || 800}
                        onChange={e => setCfg({ ...cfg, max_tokens: parseInt(e.target.value) || 800 })}
                        data-testid="chatbot-max-tokens"
                        className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary"/>
                </Field>
                <Field label="Behavioural toggles">
                    <div className="flex gap-3 flex-wrap text-[10px]">
                        <label className="inline-flex items-center gap-1.5">
                            <input type="checkbox" checked={!!cfg.multilingual} data-testid="chatbot-multilingual"
                                onChange={e => setCfg({ ...cfg, multilingual: e.target.checked })}
                                className="w-3.5 h-3.5 accent-primary"/>
                            Multilingual auto-detect
                        </label>
                        <label className="inline-flex items-center gap-1.5">
                            <input type="checkbox" checked={!!cfg.typo_correction} data-testid="chatbot-typo"
                                onChange={e => setCfg({ ...cfg, typo_correction: e.target.checked })}
                                className="w-3.5 h-3.5 accent-primary"/>
                            Silently fix user typos
                        </label>
                        <label className="inline-flex items-center gap-1.5">
                            <input type="checkbox" checked={!!cfg.learn_after_release} data-testid="chatbot-learn-auto"
                                onChange={e => setCfg({ ...cfg, learn_after_release: e.target.checked })}
                                className="w-3.5 h-3.5 accent-primary"/>
                            Self-learn after takeover release
                        </label>
                    </div>
                </Field>
            </div>

            <Field label="Greeting" hint="First bubble shown when a user opens the chat widget.">
                <textarea value={cfg.greeting || ""} rows={3}
                    onChange={e => setCfg({ ...cfg, greeting: e.target.value })}
                    data-testid="chatbot-greeting"
                    className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary"/>
            </Field>

            <Field label="Fallback message" hint="Shown when the AI errors out or has no answer.">
                <textarea value={cfg.fallback_message || ""} rows={2}
                    onChange={e => setCfg({ ...cfg, fallback_message: e.target.value })}
                    data-testid="chatbot-fallback"
                    className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary"/>
            </Field>

            <Field label="Base system prompt override"
                hint="Leave empty to use Law OS's ISO-grade default (recommended). If you fill this in it REPLACES the entire base prompt, so include privacy + style rules yourself.">
                <textarea value={cfg.system_prompt_base || ""} rows={10}
                    onChange={e => setCfg({ ...cfg, system_prompt_base: e.target.value })}
                    data-testid="chatbot-prompt-base"
                    placeholder="(empty = use default ISO-grade prompt)"
                    className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary font-mono"/>
            </Field>

            <Field label="Extra system prompt" hint="Appended to the base system prompt. Use for company-wide rules, escalation policies, brand-voice notes.">
                <textarea value={cfg.system_prompt_extra || ""} rows={6}
                    onChange={e => setCfg({ ...cfg, system_prompt_extra: e.target.value })}
                    data-testid="chatbot-prompt-extra"
                    placeholder="e.g. Always end with: For urgent issues call +91-XXXXX-XXXXX."
                    className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary font-mono"/>
            </Field>

            <div className="border border-border p-3 bg-card">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <div className="text-xs text-foreground">Preview full assembled prompt</div>
                        <div className="text-[10px] text-muted-foreground">Exactly what OpenAI receives — base + role context + knowledge base + extras + learned tone.</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <select value={previewRole} onChange={e => setPreviewRole(e.target.value)}
                            data-testid="chatbot-preview-role"
                            className="bg-background border border-border px-2 py-1.5 text-[10px]">
                            {["junior_advocate","senior_advocate","firm_owner","managing_partner","paralegal","firm_admin","super_admin"].map(r =>
                                <option key={r} value={r}>{r}</option>)}
                        </select>
                        <button onClick={() => loadPreview(previewRole)} data-testid="chatbot-preview-btn"
                            className="px-3 py-1.5 border border-primary text-primary text-[10px] uppercase tracking-[0.16em] hover:bg-primary/10">
                            {previewOpen ? "Refresh" : "Preview"}
                        </button>
                    </div>
                </div>
                {previewOpen && preview && (
                    <pre data-testid="chatbot-preview-text"
                        className="bg-background border border-border p-3 text-[10px] text-muted-foreground whitespace-pre-wrap max-h-96 overflow-y-auto font-mono">{preview.prompt}</pre>
                )}
                {previewOpen && preview && (
                    <div className="text-[10px] text-muted-foreground mt-1">
                        {preview.char_count.toLocaleString()} characters · base prompt {preview.is_overridden ? "overridden" : "default"}
                    </div>
                )}
            </div>

            <div className="border border-border p-3 bg-card">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <div className="text-xs text-foreground">Self-learned tone notes</div>
                        <div className="text-[10px] text-muted-foreground">
                            Anonymised style hints distilled from your team&apos;s admin replies.
                            {cfg.learned_notes_updated_at && (<> Last learned: {fmtDateTime(cfg.learned_notes_updated_at)} ({cfg.learned_notes_sample_count || 0} samples).</>)}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={runLearn} disabled={learning} data-testid="chatbot-learn-now"
                            className="px-3 py-1.5 border border-primary text-primary text-[10px] uppercase tracking-[0.16em] hover:bg-primary/10 disabled:opacity-30">
                            {learning ? "Learning…" : "Learn now"}
                        </button>
                        {cfg.learned_notes && (
                            <button onClick={clearLearned} data-testid="chatbot-learn-clear"
                                className="px-3 py-1.5 border border-border text-muted-foreground text-[10px] uppercase tracking-[0.16em] hover:text-rose-400">
                                Clear
                            </button>
                        )}
                    </div>
                </div>
                <textarea value={cfg.learned_notes || ""} rows={5}
                    onChange={e => setCfg({ ...cfg, learned_notes: e.target.value })}
                    data-testid="chatbot-learned-notes"
                    placeholder="(empty — click Learn now after some admin takeover sessions)"
                    className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary font-mono"/>
            </div>

            <div className="flex justify-end">
                <button onClick={save} disabled={saving} data-testid="chatbot-save"
                    className="bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 px-5 py-2 text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2">
                    <Save className="w-3.5 h-3.5"/>{saving ? "Saving…" : "Save config"}
                </button>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{label}</div>
            {children}
            {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
        </label>
    );
}

function Stat({ label, value, hint, testId }) {
    return (
        <div className="bg-background border border-border p-2.5" data-testid={testId} title={hint || undefined}>
            <div className="text-lg font-semibold text-primary">{value}</div>
            <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground mt-0.5">{label}</div>
        </div>
    );
}
